/**
 * ai-chat-storage — shared constants + helpers for AI chat persistence.
 *
 * TASK-FEAT-AI-MEMORY-P2 (2026-11-05): the AIChatWidget (floating
 * widget) and the full-page chat (`/app/asistentes-ia/[assistantId]`)
 * both need to persist conversationId + message history to localStorage
 * so the IA "remembers" across page reloads. Before this module, only
 * the AIChatWidget had the logic — the full-page chat was forgetting
 * the conversationId on every reload (smoke test caught it).
 *
 * Single source of truth for:
 *   - localStorage key prefixes (history + conversationId).
 *   - MAX_HISTORY (20 messages — mirrors the BE MAX_HISTORY_MESSAGES).
 *   - load/save helpers (try/catch with non-blocking errors).
 *
 * Anti-P-382: NO parallel mechanism — both chat UIs use these helpers.
 * Anti-P-041: 0 `as any`.
 * Anti-P-042: 0 `console.*` — errors are silently swallowed
 *             (localStorage may be full or unavailable — non-blocking).
 * Anti-P-013: n/a (localStorage failures are non-fatal — the chat
 *             still works, just without persistence).
 */

import type { AIChatMessage } from '@profileia/shared';

export const STORAGE_KEY_PREFIX = 'ai:chat:history:';
export const CONV_KEY_PREFIX = 'ai:chat:conv:';
export const MAX_HISTORY = 20;

/**
 * Build the localStorage key for the message history of a given assistant.
 */
export function historyStorageKey(assistantId: string): string {
  return `${STORAGE_KEY_PREFIX}${assistantId}`;
}

/**
 * Build the localStorage key for the conversationId of a given assistant.
 */
export function conversationStorageKey(assistantId: string): string {
  return `${CONV_KEY_PREFIX}${assistantId}`;
}

/**
 * Load the persisted message history for a given assistant.
 * Returns [] if the key is missing, the JSON is corrupt, or localStorage
 * is unavailable. The caller owns the array — mutations don't persist
 * back (use `saveHistory` for that).
 */
export function loadHistory(assistantId: string): AIChatMessage[] {
  try {
    const stored = localStorage.getItem(historyStorageKey(assistantId));
    if (!stored) return [];
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_HISTORY) as AIChatMessage[];
  } catch {
    return [];
  }
}

/**
 * Persist the message history for a given assistant. Trims to the last
 * MAX_HISTORY messages to keep localStorage bounded. Silently fails on
 * storage errors (full quota, unavailable in private mode, etc.) — the
 * chat keeps working without persistence.
 */
export function saveHistory(assistantId: string, messages: AIChatMessage[]): void {
  if (messages.length === 0) return;
  try {
    localStorage.setItem(
      historyStorageKey(assistantId),
      JSON.stringify(messages.slice(-MAX_HISTORY)),
    );
  } catch {
    // non-blocking
  }
}

/**
 * Load the persisted conversationId for a given assistant. Returns
 * null if the key is missing or localStorage is unavailable.
 */
export function loadConversationId(assistantId: string): string | null {
  try {
    return localStorage.getItem(conversationStorageKey(assistantId));
  } catch {
    return null;
  }
}

/**
 * Persist the conversationId for a given assistant. Silently fails on
 * storage errors.
 */
export function saveConversationId(assistantId: string, conversationId: string): void {
  try {
    localStorage.setItem(conversationStorageKey(assistantId), conversationId);
  } catch {
    // non-blocking
  }
}

/**
 * Clear both the history + conversationId for a given assistant.
 * Used by the "clear chat" button.
 */
export function clearChat(assistantId: string): void {
  try {
    localStorage.removeItem(historyStorageKey(assistantId));
    localStorage.removeItem(conversationStorageKey(assistantId));
  } catch {
    // non-blocking
  }
}
